/**
 * How many of each part a deck needs, worked out from its answers.
 *
 * The questions, the tokens and the scope are in deck.ts; this is the rule
 * book. Every rule is Brandon Roberts's (docs/DECK-REFERENCE.md, dated there)
 * except the 10% waste — on the lumber, boards, spindles and every fastener —
 * the joist spacing and the picture-frame blocking, which are Jonathon's
 * (2026-10-11). One function per part of the deck, in
 * the order it is built.
 */

import {
  DECK_LENGTHS,
  DECK_WASTE_PCT,
  deckSquareFeet,
  joistSpacingFor,
  railingLfOf,
  type DeckAnswers,
  type DeckCountSource,
  type DeckCounts,
  type DeckLength,
  stairSet,
  type StairSet,
} from "./deck.js";

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** Joists span at most this far before another beam goes under them. */
const MAX_JOIST_SPAN_FT = 14;
/** Brandon: "Always assume 8' apart." */
const POST_SPACING_FT = 8;
/** Brandon, 2025-08-10: "Assume 2.5 bags per post" — 80 lb bags. */
const BAGS_PER_POST = 2.5;
/** Two wedge anchors hold each post base to its footing. */
const ANCHORS_PER_POST = 2;
/** Brandon, 2025-08-10: three LedgerLOKs a post on a 2×10 or 2×12 beam. */
const LEDGERLOKS_PER_POST = 3;
/** A ledger is fixed to the house with two LedgerLOKs every 16 inches. */
const LEDGERLOKS_PER_16_IN = 2;
const LEDGERLOKS_PER_BOX = 50;
/** Brandon, 2025-03-03: 8 short and 4 long screws in a 2×8/2×10 hanger. */
const SHORT_SCREWS_PER_HANGER = 8;
const LONG_SCREWS_PER_HANGER = 4;
const SCREWS_PER_BOX = 100;
/** Pressure blocking on anything more than 8 ft out (Brandon, 2025-08-11). */
const BLOCKING_EVERY_FT = 8;
/** A 5/4×6 board is 5½ in wide and laid with an eighth-inch gap. */
const BOARD_COVER_IN = 5.625;
/** Two screws where a board crosses a joist or a stair stringer. */
const SCREWS_PER_CROSSING = 2;
/** A spindle takes two screws at the top rail and two at the bottom. */
const SCREWS_PER_SPINDLE = 4;
/** Each rail post takes two screws for the top rail and two for the bottom. */
const RAIL_SCREWS_PER_POST = 4;
/** The rail cap is screwed down every 16 inches. */
const CAP_SCREW_SPACING_IN = 16;
/** #9 × 2½ in deck screws, 25 lb box. */
const SCREWS_PER_DECK_SCREW_BOX = 1823;
/** A rail post every 6 ft. */
const RAIL_POST_SPACING_FT = 6;
/** Two ½ in carriage bolts through each rail post. */
const BOLTS_PER_RAIL_POST = 2;
const BOLTS_PER_PACK = 25;
const WASHERS_PER_PACK = 50;
const NUTS_PER_PACK = 50;
/** 2×2 balusters: 1½ in wide, 3½ in apart, so a baluster every 5 in. */
const BALUSTER_SPACING_IN = 5;
/** Two boards make a tread (11 in, 10½ in of run). */
const BOARDS_PER_TREAD = 2;
/** Bottom-of-stair rail posts, set in concrete: 2 bags each (2025-06-02). */
const STAIR_POSTS_PER_SET = 2;
const BAGS_PER_STAIR_POST = 2;
/** The ledger's L flashing comes in 10 ft lengths. */
const FLASHING_PIECE_FT = 10;
/** A tension tie kit is four ties; one kit holds a ledger at both ends. */
const TENSION_TIE_KITS = 1;
/** The longest board stocked. */
const LONGEST: DeckLength = 16;

const withWaste = (n: number): number => n * (1 + DECK_WASTE_PCT / 100);
const up = (n: number): number => Math.ceil(n - 1e-9);
const round2 = (n: number): number => Math.round(n * 100) / 100;

/* -------------------------------------------------------------------------
 * Boards cut from stock
 * ---------------------------------------------------------------------- */

/**
 * The stock length to cut a run from: the shortest that is long enough.
 * A run longer than the longest board is two boards spliced over a support.
 */
export function boardsForRun(runFt: number): Map<DeckLength, number> {
  const out = new Map<DeckLength, number>();
  if (runFt <= 0) return out;
  if (runFt > LONGEST) {
    const pieces = up(runFt / LONGEST);
    for (const [length, qty] of boardsForRun(runFt / pieces)) {
      out.set(length, qty * pieces);
    }
    return out;
  }
  const length = DECK_LENGTHS.find((l) => l >= runFt - 1e-9) ?? LONGEST;
  out.set(length, 1);
  return out;
}

/** Add `times` boards of whatever a run needs into a running total. */
function addRun(
  into: Map<DeckLength, number>,
  runFt: number,
  times: number,
): void {
  if (times <= 0) return;
  for (const [length, qty] of boardsForRun(runFt)) {
    into.set(length, (into.get(length) ?? 0) + qty * times);
  }
}

/* -------------------------------------------------------------------------
 * The frame and the steps
 * ---------------------------------------------------------------------- */

/** The deck's frame, before anything is bought. */
export interface DeckFrame {
  spacingIn: number;
  /** Rows of posts and beams under the deck. */
  beamRows: number;
  postsPerRow: number;
  posts: number;
  /** Joists between the two side rims, the picture-frame sisters included. */
  hungJoists: number;
  /** Rows of blocking: the picture frame's, and pressure blocking. */
  blockingRows: number;
  blocks: number;
  blockLengthIn: number;
}

export function deckFrame(
  answers: DeckAnswers,
  roofType: string | null | undefined,
): DeckFrame {
  const w = Math.max(0, num(answers.widthFt));
  const d = Math.max(0, num(answers.depthFt));
  const sized = w > 0 && d > 0;
  const spacingIn = joistSpacingFor(roofType);

  // An attached deck hangs off the house at the back, so it needs a beam at
  // the front and one more for each span past the longest a joist will carry.
  // A freestanding deck needs a beam at both ends.
  const spans = Math.max(1, up(d / MAX_JOIST_SPAN_FT));
  const beamRows = answers.attached ? spans : spans + 1;
  const postsPerRow = up(w / POST_SPACING_FT) + 1;

  // Joists on centres across the width. The two outermost are the side rims,
  // which are 2×12; each gets a 2×10 sister so the picture-frame border has
  // something under its inside edge.
  const onCentres = Math.floor((w * 12) / spacingIn) + 1;
  const hungJoists = Math.max(0, onCentres - 2) + 2;

  // Blocking between every pair of joists: one row behind each framed end for
  // the picture frame (the front, and the back too on a freestanding deck),
  // and a row of pressure blocking every 8 ft out.
  const framedEnds = answers.attached ? 1 : 2;
  const pressureRows = Math.max(0, up(d / BLOCKING_EVERY_FT) - 1);
  const blockingRows = framedEnds + pressureRows;

  return {
    spacingIn,
    beamRows,
    postsPerRow,
    posts: sized ? beamRows * postsPerRow : 0,
    hungJoists: sized ? hungJoists : 0,
    blockingRows,
    blocks: sized ? Math.max(0, onCentres - 1) * blockingRows : 0,
    blockLengthIn: spacingIn - 1.5,
  };
}

/* -------------------------------------------------------------------------
 * The counts, part by part
 * ---------------------------------------------------------------------- */

/** What every part's rule reads. */
interface Deck {
  answers: DeckAnswers;
  frame: DeckFrame;
  w: number;
  d: number;
  area: number;
  stairSets: number;
  stairs: StairSet | null;
  set: (source: DeckCountSource, qty: number, note: string) => void;
}

const WASTE_NOTE = `+ ${DECK_WASTE_PCT}% waste`;

/** The crew: paid on the square foot, the first set of steps in the rate. */
function countLabour(deck: Deck): void {
  const { w, d, area, stairSets, set } = deck;
  set("deck_sqft", area, `${w} × ${d} ft = ${area} SF`);
  set(
    "deck_extra_stairs",
    Math.max(0, stairSets - 1),
    `${stairSets} sets of steps, the first in the rate`,
  );
}

/** Posts, tubes, bases and anchors. A low deck's posts come two to a post. */
function countFootings(deck: Deck): void {
  const { frame, set } = deck;
  const h = Math.max(0, num(deck.answers.heightFt));
  const posts = frame.posts;
  const length = DECK_LENGTHS.find((l) => l >= h);
  if (length !== undefined) {
    const perPost = length === 8 && h <= 3 ? 0.5 : 1;
    const cut = perPost < 1 ? ", two cut from each" : "";
    set(
      `deck_post_${length}`,
      posts * perPost,
      `${frame.beamRows} row(s) × ${frame.postsPerRow} posts${cut}`,
    );
  }
  set("deck_footing_tube", posts, `one per post (${posts})`);
  set("deck_post_base", posts, `one per post (${posts})`);
  set(
    "deck_wedge_anchor",
    withWaste(posts * ANCHORS_PER_POST),
    `${ANCHORS_PER_POST} per post base ${WASTE_NOTE}`,
  );
}

/**
 * The frame lumber, cut to length. 2×12: the ledger, the two sides, every beam
 * doubled, and the stair stringers. 2×10: the joists, and the blocking cut
 * from 16 ft boards.
 */
function countFrameLumber(deck: Deck): void {
  const { answers, frame, w, d, stairs, stairSets, set } = deck;
  const twelve = new Map<DeckLength, number>();
  const ten = new Map<DeckLength, number>();
  if (answers.attached) addRun(twelve, w, 1);
  addRun(twelve, d, 2);
  addRun(twelve, w, frame.beamRows * 2);
  if (stairs) addRun(twelve, stairs.stringerFt, stairs.stringers * stairSets);
  addRun(ten, d, frame.hungJoists);

  const blocksPerBoard = Math.max(1, Math.floor(192 / frame.blockLengthIn));
  const blockBoards = frame.blocks / blocksPerBoard;
  const stringers = stairs ? ", stair stringers" : "";
  const joists = `joists at ${frame.spacingIn}" on centre`;

  for (const l of DECK_LENGTHS) {
    set(
      `deck_2x12_${l}`,
      withWaste(twelve.get(l) ?? 0),
      `ledger, sides, doubled beams${stringers} ${WASTE_NOTE}`,
    );
    const blocks = l === 16 ? blockBoards : 0;
    set(
      `deck_2x10_${l}`,
      withWaste((ten.get(l) ?? 0) + blocks),
      blocks > 0
        ? `${joists}, ${frame.blocks} blocks ${WASTE_NOTE}`
        : `${joists} ${WASTE_NOTE}`,
    );
  }
}

/** Hangers at both ends of every hung joist; LedgerLOKs; the ledger's ties and flashing. */
function countHardware(deck: Deck): void {
  const { answers, frame, w, set } = deck;
  const hangers = frame.hungJoists * 2;
  set("deck_joist_hanger", hangers, `${frame.hungJoists} joists, both ends`);
  set(
    "deck_hanger_screw_short_box",
    withWaste(hangers * SHORT_SCREWS_PER_HANGER) / SCREWS_PER_BOX,
    `${SHORT_SCREWS_PER_HANGER} per hanger ${WASTE_NOTE}`,
  );
  set(
    "deck_hanger_screw_long_box",
    withWaste(hangers * LONG_SCREWS_PER_HANGER) / SCREWS_PER_BOX,
    `${LONG_SCREWS_PER_HANGER} per hanger ${WASTE_NOTE}`,
  );

  const ledger = answers.attached
    ? up((w * 12) / 16) * LEDGERLOKS_PER_16_IN
    : 0;
  const ledgerLoks = frame.posts * LEDGERLOKS_PER_POST + ledger;
  set(
    "deck_ledgerlok_box",
    withWaste(ledgerLoks) / LEDGERLOKS_PER_BOX,
    `${ledgerLoks} screws ${WASTE_NOTE}, ${LEDGERLOKS_PER_BOX} a box`,
  );
  set("deck_nail_box", 1, "one box of framing nails");

  if (!answers.attached) return;
  set(
    "deck_tension_tie_kit",
    TENSION_TIE_KITS,
    "holds the ledger to the house",
  );
  set(
    "deck_ledger_flashing",
    withWaste(w) / FLASHING_PIECE_FT,
    `${w} ft of ledger ${WASTE_NOTE}`,
  );
}

/** The boards, picture-frame border and treads included. */
function countBoards(deck: Deck): void {
  const { answers, area, stairs, stairSets, set } = deck;
  const boardLf = (area * 12) / BOARD_COVER_IN;
  const perTreadBoard = Math.max(
    1,
    Math.floor(LONGEST / Math.max(1, num(answers.stairWidthFt))),
  );
  const treads = stairs ? stairs.treads * stairSets : 0;
  const treadBoards = (treads * BOARDS_PER_TREAD) / perTreadBoard;
  const deckBoards = up(withWaste(boardLf) / LONGEST);
  set(
    "deck_board_16",
    deckBoards + up(withWaste(treadBoards)),
    treads > 0
      ? `${area} SF of deck and ${treads} treads ${WASTE_NOTE}`
      : `${area} SF of deck ${WASTE_NOTE}`,
  );
}

/** The railing, before waste: its length, its posts and its spindles. */
interface Railing {
  railLf: number;
  railPosts: number;
  /** At the foot of the steps, set in concrete. */
  stairPosts: number;
  spindles: number;
}

/**
 * Railing round the deck and down both sides of every set of steps, only when
 * railing is required. Jonathon, 2026-10-13: "no railing means no handrails
 * either".
 */
function railingOf(deck: Deck): Railing {
  const { answers, stairSets } = deck;
  const stairs = answers.railingRequired ? deck.stairs : null;
  const deckRail = railingLfOf(answers);
  const railLf = round2(
    deckRail + (stairs ? stairs.stringerFt * 2 * stairSets : 0),
  );
  const stairPosts = stairs ? STAIR_POSTS_PER_SET * stairSets : 0;
  const railPosts =
    (deckRail > 0 ? up(deckRail / RAIL_POST_SPACING_FT) + 1 + stairSets : 0) +
    stairPosts;
  return {
    railLf,
    railPosts,
    stairPosts,
    spindles: (railLf * 12) / BALUSTER_SPACING_IN,
  };
}

function countRailing(deck: Deck, railing: Railing): void {
  const { set } = deck;
  const { railLf, railPosts, spindles } = railing;
  const bolts = withWaste(railPosts * BOLTS_PER_RAIL_POST);
  const n = railPosts * BOLTS_PER_RAIL_POST;

  set("deck_rail_post", railPosts, `every ${RAIL_POST_SPACING_FT} ft`);
  set(
    "deck_carriage_bolt_pack",
    bolts / BOLTS_PER_PACK,
    `${n} bolts ${WASTE_NOTE}`,
  );
  set(
    "deck_washer_pack",
    bolts / WASHERS_PER_PACK,
    `${n} washers ${WASTE_NOTE}`,
  );
  set("deck_nut_pack", bolts / NUTS_PER_PACK, `${n} nuts ${WASTE_NOTE}`);
  set(
    "deck_rail_2x4_16",
    withWaste(railLf * 2) / LONGEST,
    `${railLf} ft of railing, top and bottom rail ${WASTE_NOTE}`,
  );
  set(
    "deck_rail_cap_2x6_16",
    withWaste(railLf) / LONGEST,
    `${railLf} ft of rail cap ${WASTE_NOTE}`,
  );
  set(
    "deck_baluster",
    withWaste(spindles),
    `one every ${BALUSTER_SPACING_IN}" ${WASTE_NOTE}`,
  );
}

/**
 * Deck screws for everything they hold: two where a board crosses a joist,
 * two where a tread board crosses a stringer, four a spindle, four a rail
 * post for the rails, and the cap every 16 inches. Jonathon, 2026-10-11:
 * "include the treads and railing in the screw count."
 */
function countDeckScrews(deck: Deck, railing: Railing): void {
  const { area, frame, stairs, stairSets, set } = deck;
  const boardLf = (area * 12) / BOARD_COVER_IN;
  const boards = (boardLf / (frame.spacingIn / 12)) * SCREWS_PER_CROSSING;
  const treads = stairs
    ? stairs.treads *
      stairSets *
      BOARDS_PER_TREAD *
      stairs.stringers *
      SCREWS_PER_CROSSING
    : 0;
  const rails =
    railing.spindles * SCREWS_PER_SPINDLE +
    railing.railPosts * RAIL_SCREWS_PER_POST +
    (railing.railLf * 12) / CAP_SCREW_SPACING_IN;
  const screws = up(boards + treads + rails);
  set(
    "deck_screw_box",
    withWaste(screws) / SCREWS_PER_DECK_SCREW_BOX,
    `${screws} screws for the boards, treads and railing ${WASTE_NOTE}`,
  );
}

/** Concrete for the posts and the stair posts; a 2×6 behind each stair's stringers. */
function countConcreteAndStairs(deck: Deck, stairPosts: number): void {
  const { frame, stairs, stairSets, set } = deck;
  const stair = stairPosts ? `, ${BAGS_PER_STAIR_POST} a stair post` : "";
  set(
    "deck_concrete_bag",
    frame.posts * BAGS_PER_POST + stairPosts * BAGS_PER_STAIR_POST,
    `${BAGS_PER_POST} bags a post${stair}`,
  );
  // Brandon, 2025-04-08: "a 2x6 behind the tail of the stringers".
  if (stairs) {
    set("deck_stair_support_2x6_8", stairSets, "one per set of steps");
  }
}

/**
 * Every count a deck template line can take, and how it was worked out.
 *
 * A deck with no size counts nothing — the form says so before generating.
 */
export function deckCounts(
  answers: DeckAnswers,
  roofType: string | null | undefined,
): DeckCounts {
  const counts: DeckCounts = {};
  const area = deckSquareFeet(answers);
  if (area <= 0) return counts;

  const stairSets = Math.max(0, Math.round(num(answers.stairSets)));
  const deck: Deck = {
    answers,
    frame: deckFrame(answers, roofType),
    w: Math.max(0, num(answers.widthFt)),
    d: Math.max(0, num(answers.depthFt)),
    area,
    stairSets,
    stairs: stairSets > 0 ? stairSet(answers) : null,
    set: (source, qty, note) => {
      // Parts are bought whole; the crew is paid on the deck's real area.
      const n = source === "deck_sqft" ? round2(qty) : up(qty);
      if (n > 0) counts[source] = { qty: n, note };
    },
  };

  countLabour(deck);
  countFootings(deck);
  countFrameLumber(deck);
  countHardware(deck);
  countBoards(deck);
  const railing = railingOf(deck);
  countRailing(deck, railing);
  countDeckScrews(deck, railing);
  countConcreteAndStairs(deck, railing.stairPosts);
  return counts;
}
